import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { doc, updateDoc, getDoc } from "firebase/firestore";
import { db } from "../../../firebaseConfig";
import { toast } from "react-toastify";
import { getUserDetailsById, sendNotification, sendNotificationFunction } from "../../../helper/functions";
import { setSelectedPackage } from "../../../store/commonSlice";

export const useShippingRequestTracking = () => {
  const dispatch = useDispatch();
  const { selectedRequest: userActiveData, selectedPackage: packageActiveData, activeComponent,selectedItem } = useSelector((state) => state.common);
  
  // Local state
  const [packageItems, setPackageItems] = useState([]);
  const [barcodeValue, setBarcodeValue] = useState("");
  const [slipValue, setSlipValue] = useState("");
  const [dateForSlip, setDateForSlip] = useState();

  // Generate random values for barcode and slip
  const generateRandomBarcodeValue = () => {
    return Math.floor(100000000 + Math.random() * 900000000).toString();
  };

  const generateSlipNumber = () => {
    return Math.floor(100000 + Math.random() * 900000);
  };

  // Initialize values
  useEffect(() => {
    setBarcodeValue(generateRandomBarcodeValue());
    setSlipValue(generateSlipNumber());
  }, []);

  // Set slip date when package arrives
  useEffect(() => {
    if (packageActiveData?.arrivedAt?.seconds) {
      const date = new Date(packageActiveData.arrivedAt.seconds * 1000);
      const formattedDate = date.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric"
      }).replace(/\//g, ".");
      setDateForSlip(formattedDate);
    }
  }, [packageActiveData?.arrivedAt]);

  // Fetch package items
  useEffect(() => {
    const fetchPackageItems = async () => {
      if (!packageActiveData?.packageItemIds?.length) {
        setPackageItems([]);
        return;
      }

      try {
        const itemPromises = packageActiveData.packageItemIds.map(async (itemId) => {
          if (!itemId) return null;
          const itemDocRef = doc(db, "items", itemId);
          const itemDocSnap = await getDoc(itemDocRef);
          return itemDocSnap.exists() ? itemDocSnap.data() : null;
        });

        const items = await Promise.all(itemPromises);
        setPackageItems(items.filter(Boolean));
      } catch (error) {
        console.error("Error fetching package items:", error);
        setPackageItems([]);
      }
    };

    fetchPackageItems();
  }, [packageActiveData?.packageItemIds]);

  // Handle prepare shipment
  const handlePrepareShipment = async (packageId) => {
    try {
      const packageRef = doc(db, "shelfPackages", packageId);
      await updateDoc(packageRef, {
        packageStatus: "PREPARE_SHIPMENT_REQUEST_APPROVED",
        preparedShipmentDate: new Date(),
      });
      const newPackageStatus = packageActiveData?.packageStatus === "RE_PREPARE_PACKAGE" ? "RE_PREPARE_PACKAGE_COMPLETED" : "PREPARE_SHIPMENT_REQUEST_APPROVED";
      dispatch(setSelectedPackage({
        ...packageActiveData, packageStatus: newPackageStatus,
        preparedShipmentDate: new Date(),
      }));
      const user = await getUserDetailsById(packageActiveData.userId);
      await sendNotificationFunction({
        eventType: "PACKAGE",
        eventStatus: "PREPARE_SHIPMENT_REQUEST_APPROVED",
        fcmToken: user.fcmToken,
        userId: packageActiveData.userId,
        payload:{
          packageId:packageActiveData.id,
        }
      });
      // // Send notification
      // await sendNotification(
      //   packageActiveData.userId,
      //   "Shipment Prepared",
      //   `Your shipment request ${packageActiveData.id} has been prepared and is ready for shipping.`,
      //   {
      //     number: packageActiveData.id,
      //     type: "SHIPPING"
      //   }
      // );

      toast.success("Shipment prepared successfully!");
    } catch (error) {
      console.error("Error preparing shipment:", error);
      toast.error("Failed to prepare shipment");
    }
  };

  // Handle slip download
  const handleSlipDownload = async () => {
    try {
      const { default: html2canvas } = await import("html2canvas");
      const { default: jsPDF } = await import("jspdf");
      
      const slipElement = document.getElementById("receiving-slip");
      if (!slipElement) return;

      const canvas = await html2canvas(slipElement, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`${packageActiveData?.suiteNumber || "package"}_slip.pdf`);
    } catch (error) {
      console.error("Error generating slip:", error);
      toast.error("Failed to generate slip");
    }
  };

  return {
    activeComponent,
    selectedItem,
    packageActiveData,
    userActiveData,
    packageItems,
    barcodeValue,
    slipValue,
    dateForSlip,
    handlePrepareShipment,
    handleSlipDownload,
  };
}; 